import { Button, TextField, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { MOBILE_WIDTH, ROWS_PER_PAGE } from "../App";
import { AppDialog } from "../components/AppDialog";
import { AppToolbar } from "../components/AppToolbar";
import { ButtonComponent } from "../components/ButtonComponent";
import { DateFieldComponent } from "../components/DatePicker";
import { EditRoleUser } from "../components/EditRoleUser";
import { SelectComponent } from "../components/SelectComponent";
import {
  NumberField,
  SelectFieldComponent,
  TextFieldComponent,
} from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import InputValidationUtils from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/InputValidationUtils";
import useUsersList from "../hooks/useUsersList";
import useWindowSize from "../hooks/useWindowSize";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import { Routes } from "../types/Routes";
import { countries } from "../utils/Countries";
import { RoleOptions } from "../utils/enums/EnsolversStaffRoles";
import { UserStatusFilters } from "../utils/enums/filters/UserStatusFilters";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import useTableHeight from "../hooks/useTableHeight";
import useUpdateUrl from "../hooks/useUpdateUrl";
import WelcomeFormFields from "../components/PersonalInfo/WelcomeFormFields";
import ActionButton, { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";
import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";
import { useToolbarActions } from "../components/Main/ToolbarActions";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import InfoIcon from "@mui/icons-material/Info";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import EditIcon from "@mui/icons-material/Edit";
import { EditDialog } from "../external/ensolvers-core-frontend-mui/components/EditDialog";
import { DialogPosition } from "../external/ensolvers-core-frontend-mui/components/Common/DialogPosition";
import { useStyles as formStyles } from "../components/Main/formStyles";
import { Box } from "@mui/material";
import { filtersBarStyle, Token } from "../theme";
import { Theme } from "@mui/material/styles";

export default function UsersList() {
  const { toolbarActionsSlot, addButtonInToolbar } = useToolbarActions();
  const { t } = useTranslation();

  const [isEditModeActive, setIsEditModeActive] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isEditingRoles, setIsEditingRoles] = useState(false);
  const [isEditingEnabDisab, setIsEditingEnabDisab] = useState(false);
  const [staffRole, setStaffRole] = useState<string>("All");
  const [userStatus, setUserStatus] = useState<string>(UserStatusFilters.ALL);
  const {
    search,
    setSearch,
    forceUpdateOn,
    onDataFetch,
    getRoles,
    setSelectedUser,
    goPersonalInfoPage,
    setRolesForUser,
    handleRoleChange,
    selectedUser,
    handleUserStatusChange,
    handleUserCreation,
    handleUserUpdate,
    roles,
  } = useUsersList(staffRole, userStatus);
  const classes = useStyles();
  const { isMobileWidth } = useWindowSize();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();
  const searchParams = new URLSearchParams(useLocation().search);
  const nameFilter = searchParams.get("name");
  const roleFilter = searchParams.get("role");
  const statusFilter = searchParams.get("status");
  const { updateUrl } = useUpdateUrl(Routes.USERS, searchParams);
  const customStyles = formStyles();

  useEffect(() => {
    if (roleFilter !== null) {
      setStaffRole(roleFilter);
    } else {
      searchParams.set("role", staffRole);
    }

    if (statusFilter !== null) {
      setUserStatus(statusFilter);
    } else {
      searchParams.set("status", userStatus);
    }

    if (nameFilter !== null && nameFilter !== "") {
      setSearch(nameFilter);
    }
    updateUrl();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedUser === undefined) {
      setIsEditingEnabDisab(false);
      setIsEditingRoles(false);
      setIsEditingUser(false);
      setIsEditing(false);
    }
  }, [selectedUser]);

  function getAdditionalActions(instance: EnsolversOpsUser): ActionsMenuEntryIcons[] {
    const options = [
      {
        title: t(instance.enabled ? "Disable User" : "Enable User"),
        action: () => {
          setSelectedUser(instance);
          setIsEditingEnabDisab(true);
        },
        icon: <PersonOutlineIcon />,
      },
      {
        title: t("Personal info"),
        action: () => goPersonalInfoPage(instance),
        icon: <InfoIcon />,
      },
      {
        title: t("Edit roles"),
        action: () => {
          setIsEditingRoles(true);
          setRolesForUser(instance);
          setSelectedUser(instance);
        },
        icon: <ManageAccountsIcon />,
      },
      {
        title: t("Edit"),
        action: () => {
          setSelectedUser(instance);
          setIsEditingUser(true);
        },
        icon: <EditIcon />,
      },
    ];

    return options;
  }

  function handleOnCancel(): void {
    setSelectedUser(undefined);
  }

  return (
    <AppContainerFullHeight
      bottomSectionRef={bottomSectionRef}
      topSection={
        <AppToolbar title="Users" rightContent={toolbarActionsSlot}>
          <div className={classes.container}>
            <TextField
              className="user-name-textfield"
              label="User Name"
              name="username"
              variant="outlined"
              value={search}
              size="small"
              onChange={(e) => {
                setSearch(e.target.value);
                searchParams.set("name", e.target.value);
                updateUrl();
              }}
              style={{ marginLeft: "20px", marginRight: "20px", minWidth: "400px" }}
            />
            <SelectComponent
              label={"Roles"}
              name="roles"
              value={staffRole ?? "All"}
              setValue={(role: string) => {
                setStaffRole(role);
                searchParams.set("role", role);
                updateUrl();
              }}
              options={Object.values(RoleOptions)}
              size="small"
            />
            <SelectComponent
              label={"Status"}
              name="status"
              value={userStatus}
              setValue={(status: string) => {
                setUserStatus(status);
                searchParams.set("status", status);
                updateUrl();
              }}
              options={Object.values(UserStatusFilters)}
              size="small"
            />
            <ButtonComponent
              size="small"
              onClick={() => {
                setSearch("");
                setStaffRole(RoleOptions.ALL);
                setUserStatus(UserStatusFilters.ALL);
                searchParams.set("name", "");
                searchParams.set("role", RoleOptions.ALL);
                searchParams.set("status", UserStatusFilters.ALL);
                updateUrl();
              }}
              style={{ marginRight: "80px", marginLeft: "80px", height: "40px" }}
              text={"Remove Filters"}
            />
          </div>
        </AppToolbar>
      }
      bottomSection={
        <Box sx={{ borderRadius: 8 }}>
          <TableBasedCrud<EnsolversOpsUser>
            headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
            buttonStyles={{ marginRight: 20, color: "white", backgroundColor: Token.actionPrimary, marginBottom: 10 }}
            cancelButtonStyles={{
              color: Token.textSecondary,
              border: `1px solid ${Token.borderDefault}`,
              marginBottom: 10,
            }}
            dialogStyles={customStyles.root}
            buttonContainer={customButtonsContainer}
            gridButtonContainer={gridButtonContainer}
            addButtonBuilder={addButtonInToolbar(customButtonBuilder)}
            maxHeight={calculateMaxHeight(true)}
            stickyHeader
            stickyPagination
            elementName={t("User")}
            rowsPerPage={ROWS_PER_PAGE}
            forceUpdateOn={forceUpdateOn}
            disableAutoFetch
            denseTable
            setIsEditModeActive={setIsEditModeActive}
            onDataFetch={onDataFetch}
            onCreate={handleUserCreation}
            onUpdate={handleUserUpdate}
            delayFetch={search}
            renderDialogContent={(register, errors, control) => (
              <>
                <TextFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"firstName"}
                  label="First name"
                  required={true}
                />
                <TextFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"lastName"}
                  label="Last name"
                  required={true}
                />
                <TextFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"email"}
                  label="Email"
                  required={true}
                  pattern={InputValidationUtils.emailRegex()}
                />
                <TextFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  inputType="password"
                  propertyName={"plainPassword"}
                  label={isEditModeActive ? "Set New Password" : "Password"}
                  required={false}
                />
                <NumberField
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"phone"}
                  label="Phone"
                  required={true}
                  pattern={InputValidationUtils.phoneRegex()}
                />
                <DateFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  format={"dd/MMM/yyyy"}
                  propertyName={"shortStart"}
                  label="Start Date"
                  required={true}
                  clearable={true}
                  toUTC
                />
                <DateFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  format={"dd/MMM"}
                  propertyName={"shortBirthday"}
                  label="Day of Birth"
                  required={true}
                  clearable={true}
                  toUTC
                  disableToolbar
                />
                <SelectFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"country"}
                  label={"Country"}
                  defaultValue={"ARGENTINA"}
                  required={false}
                  options={countries}
                />
                <TextFieldComponent control={control} register={register} errors={errors} propertyName={"region"} label="Region" />
                <NumberField
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"additionalVacationsAmount"}
                  label="Additional Vacations Amount (days)"
                  inputType="number"
                  required
                  defaultValue="0"
                  pattern={InputValidationUtils.numberRegex()}
                />
                <DateFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  format={"dd/MMM/yyyy"}
                  propertyName={"vacationsComputedSince"}
                  label="Vacations Computed Since"
                  required={true}
                  clearable={true}
                  toUTC
                />
                <NumberField
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"initialTakenVacations"}
                  label="Initial Taken Vacations (days)"
                  inputType="number"
                  required
                  defaultValue="0"
                  pattern={InputValidationUtils.numberRegex()}
                />
                <NumberField
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"vacationAccumulation"}
                  label="Vacation Accumulation per year (days)"
                  inputType="number"
                  required
                  defaultValue="10"
                  pattern={InputValidationUtils.numberRegex()}
                />

                <WelcomeFormFields control={control} register={register} errors={errors} />
              </>
            )}
            resetValue={{ firstName: "", lastName: "", email: "", plainPassword: "", phone: "", enabled: "", start: null, birthday: null }}
            tableColumns={[
              {
                header: t("Name"),
                content: (instance) => (
                  <>
                    <Link to={`${Routes.WORKLOG}?user=${instance.coreUserExternalId}`}>{`${instance.firstName} ${instance.lastName}`}</Link>
                  </>
                ),
              },
              {
                header: t("Email"),
                content: (instance) => <>{instance.email}</>,
              },
              {
                header: t("Role"),
                content: (instance) => <>{instance.roles ? getRoles(instance.roles) : ""}</>,
              },
              {
                header: t("Status"),
                content: (instance) => (
                  <Button
                    variant="outlined"
                    disabled
                    style={{
                      textTransform: "none",
                      color: "white",
                      backgroundColor: instance.enabled ? "green" : "orange",
                      maxWidth: "150px",
                    }}
                    fullWidth
                  >
                    {instance.enabled ? "Enabled" : "Disabled"}
                  </Button>
                ),
              },
              {
                header: t("Actions"),
                content: (instance) => {
                  const actions = getAdditionalActions(instance);
                  return (
                    <>
                      {actions.map((action) => {
                        return <ActionButton onClick={action.action} tooltip={action.title} icon={action.icon} />;
                      })}
                    </>
                  );
                },
              },
            ]}
            hideActions={true} // Hide default Actions column
            buildAdditionalActions={(user) => {
              return [
                {
                  title: t(user.enabled ? "Disable User" : "Enable User"),
                  action: () => setSelectedUser(user),
                },
                {
                  title: t("Personal info"),
                  action: () => goPersonalInfoPage(user),
                },
                {
                  title: t("Edit roles"),
                  action: () => {
                    setIsEditing(true);
                    setRolesForUser(user);
                    setSelectedUser(user);
                  },
                },
              ];
            }}
          />

          {/* Dialog to Enable/Disable users */}
          <AppDialog
            open={!!selectedUser && !isEditing && isEditingEnabDisab}
            onClose={handleOnCancel}
            title={selectedUser?.enabled ? "Disable User" : "Enable User"}
            content={
              <>
                <Typography>
                  {`The next user is going to be ${selectedUser?.enabled ? "disabled" : "enabled"}: ${selectedUser?.firstName} ${
                    selectedUser?.lastName
                  }.`}
                </Typography>
                <Typography>Do you want to confirm this action?</Typography>
              </>
            }
            buttons={[{ label: `Confirm ${selectedUser?.enabled ? "Disable" : "Enable"}`, onClick: () => handleUserStatusChange() }]}
          />

          {/* Dialog to edit roles */}
          <EditRoleUser
            selectedUser={selectedUser}
            isEditing={isEditingRoles && !isEditingEnabDisab}
            setSelectedUser={setSelectedUser}
            setIsEditing={setIsEditing}
            onRoleChange={handleRoleChange}
            roles={roles}
          />

          {/* Copy of the Dialog to Edit -- To use with Edit Action Icon */}
          <EditDialog<EnsolversOpsUser>
            headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
            buttonStyles={{ marginRight: 20, color: "white", backgroundColor: Token.actionPrimary, marginBottom: 10 }}
            cancelButtonStyles={{
              color: Token.textSecondary,
              border: `1px solid ${Token.borderDefault}`,
              marginBottom: 10,
            }}
            dialogStyles={customStyles.root}
            elementName={"User"}
            dialogPosition={DialogPosition.Center}
            currentInstance={selectedUser}
            newItemDialogOpen={isEditingUser}
            onCreate={handleUserCreation}
            onUpdate={handleUserUpdate}
            onCancel={handleOnCancel}
            renderDialogContent={(register, errors, control) => (
              <>
                <TextFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"firstName"}
                  label="First name"
                  required={true}
                />
                <TextFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"lastName"}
                  label="Last name"
                  required={true}
                />
                <TextFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"email"}
                  label="Email"
                  required={true}
                  pattern={InputValidationUtils.emailRegex()}
                />
                <TextFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  inputType="password"
                  propertyName={"plainPassword"}
                  label={isEditModeActive ? "Set New Password" : "Password"}
                  required={false}
                />
                <NumberField
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"phone"}
                  label="Phone"
                  required={true}
                  pattern={InputValidationUtils.phoneRegex()}
                />
                <DateFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  format={"dd/MMM/yyyy"}
                  propertyName={"shortStart"}
                  label="Start Date"
                  required={true}
                  clearable={true}
                  toUTC
                />
                <DateFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  format={"dd/MMM"}
                  propertyName={"shortBirthday"}
                  label="Day of Birth"
                  required={true}
                  clearable={true}
                  toUTC
                  disableToolbar
                />
                <SelectFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"country"}
                  label={"Country"}
                  defaultValue={"ARGENTINA"}
                  required={false}
                  options={countries}
                />
                <TextFieldComponent control={control} register={register} errors={errors} propertyName={"region"} label="Region" />
                <NumberField
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"additionalVacationsAmount"}
                  label="Additional Vacations Amount (days)"
                  inputType="number"
                  required
                  defaultValue="0"
                  pattern={InputValidationUtils.numberRegex()}
                />
                <DateFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  format={"dd/MMM/yyyy"}
                  propertyName={"vacationsComputedSince"}
                  label="Vacations Computed Since"
                  required={true}
                  clearable={true}
                  toUTC
                />
                <NumberField
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"initialTakenVacations"}
                  label="Initial Taken Vacations (days)"
                  inputType="number"
                  required
                  defaultValue="0"
                  pattern={InputValidationUtils.numberRegex()}
                />
                <NumberField
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"vacationAccumulation"}
                  label="Vacation Accumulation per year (days)"
                  inputType="number"
                  required
                  defaultValue="10"
                  pattern={InputValidationUtils.numberRegex()}
                />

                <WelcomeFormFields control={control} register={register} errors={errors} />
              </>
            )}
            setNewInstanceDialogOpen={() => {}}
            setLastUpdateTimestamp={() => {}}
          />
        </Box>
      }
    />
  );
}

const useStyles = makeStyles<Theme, {}, string>(
  // @ts-ignore -- computed media-query keys are valid JSS but not StyleRules
  (theme) => ({
    container: {
      ...filtersBarStyle(),
      "&>div, button": {
        margin: "0 !important",
      },
      "& .user-name-textfield": {
        width: "100%",
        minWidth: "125px !important",
        maxWidth: "300px",
      },
    },
    [theme.breakpoints.down(MOBILE_WIDTH)]: {
      container: {
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gridRowGap: "10px",
        "& button": {
          padding: "0 !important",
          justifySelf: "left",
          height: "35px !important",
        },
      },
    },
  }),
  { index: 1 },
);
